import type { CharacterSheetInterface } from '@/types/characters';
import type {
  AppliedConfiguration,
  RandomTableInterface,
  RandomTableResultInterface,
  SwadeModConfigurationInterface,
  SwadeModificationInterface,
} from '@/types/items';
import type { WorldInterface } from '@/types/worlds';
import { resolveTablesTabLabel as resolveWorldTablesTabLabel } from '@/appdata/worldTables';
import { deepCopyObject } from '@/appdata/globals';
import { resolvedModificationUuid } from '@/appdata/applied-configuration';
import DieRoll from '@/types/die-roll';

export interface TableOption {
  uuid: string;
  label: string;
}

export interface TableRollEntry {
  modification: SwadeModificationInterface;
  source: { uuid: string; name: string };
  rollName?: string | null;
}

const siblingRollName = (
  mods: SwadeModificationInterface[] | undefined,
): string | null => {
  const rn = (mods ?? []).find((m) => m?.configuration?.path === 'rollName');
  const label = rn?.configuration?.label;
  return label && label.trim().length > 0 ? label : null;
};

const entriesFromSource = (
  mods: SwadeModificationInterface[] | undefined,
  source: { uuid?: string | null; name?: string | null } | null | undefined,
): TableRollEntry[] => {
  if (!source?.uuid) return [];
  const rollName = siblingRollName(mods);
  return (mods ?? [])
    .filter((m) => m?.configuration?.path === 'tableRolls')
    .map((modification) => ({
      modification,
      source: { uuid: source.uuid as string, name: source.name ?? '' },
      rollName,
    }));
};

/**
 * Scan the character's granted sources (framework, abilities, ancestry and its
 * grants, edges) for `tableRolls` modifications, capturing a sibling `rollName`
 * label per source. Sources without a stable uuid (nothing to anchor records
 * to) are skipped. Order: framework, then abilities, then ancestry (its own
 * modifications, then its grants'), then edges.
 */
export function collectTableRolls(
  character: CharacterSheetInterface | null | undefined,
): TableRollEntry[] {
  if (!character) return [];
  const entries: TableRollEntry[] = [];

  const framework = character.framework;
  if (framework && !Array.isArray(framework)) {
    entries.push(...entriesFromSource(framework.modifications, framework));
  }

  for (const ability of character.abilities ?? []) {
    entries.push(...entriesFromSource(ability?.modifications, ability));
  }

  if (character.ancestry) {
    entries.push(...entriesFromSource(character.ancestry.modifications, character.ancestry));
  }

  for (const grant of character.ancestry?.grants ?? []) {
    entries.push(...entriesFromSource(grant?.modifications, grant));
  }

  for (const edge of character.edges ?? []) {
    const selection = edge?.selection;
    entries.push(
      ...entriesFromSource(selection?.modifications, {
        uuid: edge?.uuid ?? selection?.uuid,
        name: selection?.name,
      }),
    );
  }

  return entries;
}

/**
 * Tab label precedence: first non-blank sibling rollName → world-level rollName
 * (delegates to worldTables) → generic. Named distinctly from the worldTables
 * helper to avoid a same-name/different-signature collision.
 */
export function resolveCharacterTablesTabLabel(
  entries: TableRollEntry[],
  world: WorldInterface | null | undefined,
  generic: string,
): string {
  const sibling = entries.find((e) => e.rollName && e.rollName.trim().length > 0)?.rollName;
  if (sibling && sibling.trim().length > 0) return sibling;
  return resolveWorldTablesTabLabel(world, generic);
}

/** Table options (`configuration.element` chips) for a tableRolls modification. */
export function tableOptionsFor(
  modification: SwadeModificationInterface,
): TableOption[] {
  const element = modification?.configuration?.element;
  if (!Array.isArray(element)) return [];
  return (element as any[])
    .filter((o) => o && typeof o === 'object' && 'uuid' in o)
    .map((o: any) => ({ uuid: o.uuid, label: o.label ?? '' }));
}

/** Number of rolls a modification grants (`configuration.value`, default 1, min 1). */
export function rollCountFor(modification: SwadeModificationInterface): number {
  const raw = modification?.configuration?.value as unknown;
  const n = typeof raw === 'number' ? raw : parseInt((raw as string) ?? '', 10);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

const isAnchor = (r: AppliedConfiguration): boolean =>
  (r.configuration?.path ?? null) === null;

/** Records resolving a given modification (neutral sourceModUuid, legacy choiceSetUuid fallback). */
export function recordsForModification(
  choices: AppliedConfiguration[] | undefined,
  modUuid: string,
): AppliedConfiguration[] {
  return (choices ?? []).filter((r) => resolvedModificationUuid(r) === modUuid);
}

/** Distinct result uuids already taken for a modification. */
export function takenResultUuids(
  choices: AppliedConfiguration[] | undefined,
  modUuid: string,
): Set<string> {
  const set = new Set<string>();
  for (const r of recordsForModification(choices, modUuid)) {
    if (r.resultUuid) set.add(r.resultUuid);
  }
  return set;
}

/** The recorded pick for (modUuid, rollIndex) via its anchor, or null if unresolved. */
export function pickForRoll(
  choices: AppliedConfiguration[] | undefined,
  modUuid: string,
  rollIndex: number,
): { tableUuid: string; resultUuid: string; resultName: string; resultSummary: string | null } | null {
  const anchor = recordsForModification(choices, modUuid).find(
    (r) => r.rollIndex === rollIndex && isAnchor(r),
  );
  if (!anchor?.resultUuid || !anchor.tableUuid) return null;
  return {
    tableUuid: anchor.tableUuid,
    resultUuid: anchor.resultUuid,
    resultName: anchor.configuration?.label ?? '',
    resultSummary: (anchor.configuration as any)?.summary ?? null,
  };
}

/** The result whose [min, max] contains the roll; first match wins; null if none. */
export function findResultForRoll(
  table: RandomTableInterface,
  roll: number,
): RandomTableResultInterface | null {
  for (const result of table?.relatedElements ?? []) {
    if (roll >= (result.min ?? 0) && roll <= (result.max ?? 0)) return result;
  }
  return null;
}

/**
 * Pick a result for the dice button, preventing duplicates. Randomness is
 * injectable (DIP) for deterministic tests; production omits `deps`.
 */
export function rollUntakenResult(
  table: RandomTableInterface,
  taken: Set<string>,
  deps: {
    roll?: () => number;
    pickIndex?: (n: number) => number;
    attempts?: number;
  } = {},
): RandomTableResultInterface | null {
  const untaken = (table?.relatedElements ?? []).filter(
    (r) => r.uuid && !taken.has(r.uuid),
  );
  if (untaken.length === 0) return null;

  const roll = deps.roll ?? (() => new DieRoll(table?.notation ?? '').roll());
  const pickIndex = deps.pickIndex ?? ((n: number) => Math.floor(Math.random() * n));
  const attempts = deps.attempts ?? 20;

  for (let i = 0; i < attempts; i++) {
    const match = findResultForRoll(table, roll());
    if (match?.uuid && !taken.has(match.uuid)) return match;
  }
  return untaken[pickIndex(untaken.length)] ?? null;
}

/** Build the anchor + per-modification records for one pick (see spec Data Model). */
export function buildTableRollRecords(args: {
  sourceUuid: string;
  modUuid: string;
  tableUuid: string;
  result: RandomTableResultInterface;
  rollIndex: number;
}): AppliedConfiguration[] {
  const { sourceUuid, modUuid, tableUuid, result, rollIndex } = args;
  const tags = {
    uuid: sourceUuid,
    sourceModUuid: modUuid,
    tableUuid,
    resultUuid: result.uuid,
    rollIndex,
  };
  const anchor: AppliedConfiguration = {
    ...tags,
    configuration: {
      path: null,
      label: result.name ?? '',
      summary: result.summary ?? result.description ?? null,
    } as SwadeModConfigurationInterface,
  };
  const modRecords: AppliedConfiguration[] = (result.modifications ?? []).map(
    (m: SwadeModificationInterface) => ({
      ...tags,
      configuration: deepCopyObject(m.configuration) as SwadeModConfigurationInterface,
    }),
  );
  return [anchor, ...modRecords];
}
